import axios, { CancelTokenSource } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getExternalTransfers, getExternalTransfersByNextUrl } from './api';
import { ExternalTransferEntry, ExternalTransferResponse } from './domain';

type Options = {
  search?: string;
  shouldFetch?: boolean;
};

const useExternalTransfers = (
  options: Options = {
    search: undefined,
    shouldFetch: true,
  },
) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [list, setList] = useState<ExternalTransferEntry[]>([]);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const axiosCancelRef = useRef<CancelTokenSource>(axios.CancelToken.source());
  const search = options.search;
  const shouldFetch = options.shouldFetch ?? true;

  const getNextPage = useCallback(async () => {
    if (!nextUrl || loading) return;

    setLoading(true);
    setError(false);

    let resp: ExternalTransferResponse | undefined;
    try {
      axiosCancelRef.current = axios.CancelToken.source();
      resp = await getExternalTransfersByNextUrl(nextUrl, axiosCancelRef.current);
    } catch (e) {
      setError(true);
      setLoading(false);
      return;
    }

    // Returns undefined if the request is cancelled
    if (resp === undefined) return;

    setList((prev) => [...prev, ...resp!.transfers]);
    setNextUrl(resp.nextPageUrl);
    setLoading(false);
  }, [nextUrl, loading]);

  useEffect(() => {
    const load = async (search: string | undefined) => {
      setLoading(true);
      setError(false);

      let resp: ExternalTransferResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getExternalTransfers(search, axiosCancelRef.current);
      } catch (e) {
        setError(true);
        setLoading(false);
        return;
      }

      // Returns undefined if the request is cancelled
      if (resp === undefined) return;
      setNextUrl(resp.nextPageUrl);
      setList(resp.transfers);
      setLoading(false);
    };

    if (shouldFetch) {
      load(search);
    }

    return () => {
      // Cancel the request if it has already been executed
      axiosCancelRef.current.cancel();
    };
  }, [search, shouldFetch]);

  return { loading, error, list, getNextPage };
};

export default useExternalTransfers;
export type { ExternalTransferEntry } from './domain';
